import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import {
  FINDINGS_PAGE_SIZE_DEFAULT, SEVERITY_ORDER, type ComponentType, type Finding, type FindingStatus, type FindingsQuery, type FindingsSort,
} from '@ametica/shared';
import { findingsCsvHref, useFindingsPage } from '../api/findings';
import { useSites } from '../api/sites';
import { PageTitle } from '../layout/PageTitle';
import { Icon } from '../components/Icon';
import { SeverityBadge } from '../features/findings/SeverityBadge';
import { SEVERITY, pluralFindings } from '../features/findings/severity';
import { DEFAULT_FILTERS, hasActiveFilters, parseFilters, serializeFilters, toggleIn } from '../features/findings/filters';
import { formatDateTime, timeAgo } from '../lib/time';

const TYPE_LABEL: Record<ComponentType, string> = { plugin: 'Pluginy', theme: 'Témy', core: 'WP core' };
const TYPE_ONE: Record<ComponentType, string> = { plugin: 'plugin', theme: 'téma', core: 'core' };
const STATUS_LABEL: Record<FindingStatus, string> = { OPEN: 'otvorené', RESOLVED: 'uzavreté', IGNORED: 'ignorované', ACCEPTED_RISK: 'akceptované riziko' };
const ALL_STATUSES: FindingStatus[] = ['OPEN', 'RESOLVED', 'IGNORED', 'ACCEPTED_RISK'];
const PAGE_SIZES = [25, 50, 100];
const nf = new Intl.NumberFormat('sk');

/** Predvoľby stavu v selecte — viac hodnôt naraz vie URL (`status=OPEN,IGNORED`), select ponúka bežné pohľady. */
const STATUS_PRESETS: Array<{ key: string; label: string; value: FindingStatus[] }> = [
  { key: 'OPEN', label: 'Otvorené', value: ['OPEN'] },
  { key: 'RESOLVED', label: 'Uzavreté', value: ['RESOLVED'] },
  { key: 'IGNORED', label: 'Ignorované', value: ['IGNORED'] },
  { key: 'ACCEPTED_RISK', label: 'Akceptované riziko', value: ['ACCEPTED_RISK'] },
  { key: 'all', label: 'Všetky stavy', value: ALL_STATUSES },
];
const statusKey = (s: FindingStatus[]) => STATUS_PRESETS.find((p) => p.value.length === s.length && p.value.every((v) => s.includes(v)))?.key ?? 'custom';

/**
 * Tabuľka zraniteľností cez všetky stránky (design/_app.js V.vulns). Filtre žijú
 * v URL; backend filtruje, radí aj stránkuje — sem príde len jedna stránka a `total`.
 */
export function FindingsPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { query, invalid } = useMemo(() => parseFilters(params), [params]);
  const page = useFindingsPage(query);
  const sites = useSites();

  /** Zmena filtra = nová URL (history push, aby „späť" vrátil predchádzajúci pohľad); filtre vždy resetujú stránku. */
  const update = (patch: Partial<FindingsQuery>, keepPage = false) =>
    setParams(serializeFilters({ ...query, page: keepPage ? query.page : 1, ...patch }));
  const reset = () => setParams(serializeFilters(DEFAULT_FILTERS));

  // hľadanie píše do URL až po krátkej pauze — každý znak by inak bol requestom aj položkou histórie
  const [q, setQ] = useState(query.q ?? '');
  useEffect(() => { setQ(query.q ?? ''); }, [query.q]);
  useEffect(() => {
    const trimmed = q.trim();
    if (trimmed === (query.q ?? '')) return;
    const t = setTimeout(() => update({ q: trimmed || undefined }), 300);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const sortBy = (sort: FindingsSort) =>
    update({ sort, dir: query.sort === sort ? (query.dir === 'asc' ? 'desc' : 'asc') : sort === 'detected' || sort === 'cvss' ? 'desc' : 'asc' }, true);

  const total = page.data?.total ?? 0;
  const items = page.data?.items ?? [];
  const pages = Math.max(1, Math.ceil(total / query.pageSize));
  const from = total === 0 ? 0 : (query.page - 1) * query.pageSize + 1;
  const to = Math.min(total, query.page * query.pageSize);
  const active = hasActiveFilters(query);
  const siteName = (id: string) => sites.data?.find((s) => s.id === id)?.name ?? id;

  const tags: string[] = [
    ...(query.siteId ? [`Stránka: ${siteName(query.siteId)}`] : []),
    ...(query.severity ?? []).map((s) => `Závažnosť: ${SEVERITY[s].label}`),
    ...(query.type ?? []).map((t) => `Typ: ${TYPE_LABEL[t]}`),
    ...(statusKey(query.status) !== 'OPEN' ? [`Stav: ${query.status.map((s) => STATUS_LABEL[s]).join(', ')}`] : []),
    ...(query.patchAvailable === true ? ['Patch dostupný'] : query.patchAvailable === false ? ['Bez patchu'] : []),
    ...(query.q ? [`Hľadanie: „${query.q}"`] : []),
  ];

  return (
    <>
      <PageTitle
        title="Zraniteľnosti"
        sub={page.data ? `${nf.format(total)} ${pluralFindings(total)}${active ? ' po filtri' : ''} · ${sites.data?.length ?? '…'} stránok` : undefined}
        actions={
          <a href={findingsCsvHref(query)} download className="btn btn-g" title="CSV toho istého filtrovaného pohľadu, vrátane atribúcie MITRE/Defiant">
            Export CSV
          </a>
        }
      />

      {invalid && (
        <div className="note border-sev-medium/40 text-[12.5px] text-dim">
          <Icon name="warn" size={16} className="shrink-0 text-sev-medium" />
          <span>Odkaz obsahoval neplatný filter — zobrazujem predvolený pohľad. <button type="button" className="text-brand" onClick={reset}>Vyčistiť URL</button></span>
        </div>
      )}

      <div className="flex flex-col gap-3.5">
        <div className="flex flex-wrap items-center gap-3">
          <label className="input min-w-[230px] max-w-[340px] flex-1">
            <Icon name="search" size={15} className="shrink-0 text-faint" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hľadať plugin, CVE alebo názov…" aria-label="Hľadať" />
          </label>
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Závažnosť">
            {SEVERITY_ORDER.map((sev) => {
              const on = query.severity?.includes(sev) ?? false;
              const n = page.data?.bySeverity[sev];
              return (
                <button key={sev} type="button" className={`chip ${on ? 'on' : ''}`} aria-pressed={on} onClick={() => update({ severity: toggleIn(query.severity, sev) })}>
                  <span className={`dotst ${SEVERITY[sev].bg}`} />
                  {SEVERITY[sev].label}
                  {n !== undefined && <span className="n">{n}</span>}
                </button>
              );
            })}
          </div>
          <div className="ml-auto flex flex-wrap gap-2">
            <select className={`select ${query.siteId ? 'on' : ''}`} value={query.siteId ?? ''} aria-label="Stránka" onChange={(e) => update({ siteId: e.target.value || undefined })}>
              <option value="">Všetky stránky</option>
              {sites.data?.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select className={`select ${statusKey(query.status) !== 'OPEN' ? 'on' : ''}`} value={statusKey(query.status)} aria-label="Stav" onChange={(e) => update({ status: STATUS_PRESETS.find((p) => p.key === e.target.value)?.value ?? ['OPEN'] })}>
              {STATUS_PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              {statusKey(query.status) === 'custom' && <option value="custom">Stav: {query.status.map((s) => STATUS_LABEL[s]).join(', ')}</option>}
            </select>
            <select className={`select ${query.patchAvailable !== undefined ? 'on' : ''}`} value={query.patchAvailable === undefined ? '' : String(query.patchAvailable)} aria-label="Patch" onChange={(e) => update({ patchAvailable: e.target.value === '' ? undefined : e.target.value === 'true' })}>
              <option value="">Patch: všetky</option>
              <option value="true">Len s dostupným patchom</option>
              <option value="false">Len bez patchu</option>
            </select>
            <button type="button" className="chip" onClick={reset} disabled={!active}>Zrušiť filtre</button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Typ komponentu">
          {(Object.keys(TYPE_LABEL) as ComponentType[]).map((t) => {
            const on = query.type?.includes(t) ?? false;
            return <button key={t} type="button" className={`chip ${on ? 'on' : ''}`} aria-pressed={on} onClick={() => update({ type: toggleIn(query.type, t) })}>{TYPE_LABEL[t]}</button>;
          })}
          <span className="ml-3 text-[11.5px] text-faint">Aktívne:</span>
          {tags.length === 0
            ? <span className="text-[11.5px] text-ghost">žiadne — zobrazujem všetky otvorené nálezy</span>
            : tags.map((t) => <span key={t} className="pill border border-line-2 bg-raised text-dim">{t}</span>)}
        </div>
      </div>

      <div className="card">
        <div className="tblwrap">
          <table className="tbl">
            <thead>
              <tr>
                <Th label="ZÁVAŽNOSŤ" sort="severity" q={query} onSort={sortBy} />
                <Th label="ZRANITEĽNOSŤ" sort="component" q={query} onSort={sortBy} />
                <Th label="STRÁNKA" sort="site" q={query} onSort={sortBy} />
                <th>CVE</th>
                <Th label="CVSS" sort="cvss" q={query} onSort={sortBy} />
                <th>OPRAVA</th>
                <Th label="OD" sort="detected" q={query} onSort={sortBy} />
              </tr>
            </thead>
            <tbody className={page.isPlaceholderData ? 'opacity-60' : ''}>
              {page.isPending && <tr><td colSpan={7} className="text-faint">Načítavam…</td></tr>}
              {page.isError && <tr><td colSpan={7} className="text-sev-critical">Nálezy sa nepodarilo načítať: {page.error.message}</td></tr>}
              {page.data && items.length === 0 && (
                <tr><td colSpan={7}>
                  <div className="empty">
                    <Icon name="search" size={26} />
                    {active ? 'Žiadny nález nezodpovedá filtru' : 'Zatiaľ žiadny otvorený nález'}
                    {active && <button type="button" className="btn btn-g mt-1" onClick={reset}>Zrušiť filtre</button>}
                  </div>
                </td></tr>
              )}
              {items.map((f) => <Row key={f.id} f={f} onOpen={() => navigate(`/vulnerabilities/${f.id}`)} />)}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-5 py-3.5">
          <span className="text-[11.5px] text-faint">
            {page.data ? `Zobrazených ${from}–${to} z ${nf.format(total)} ${pluralFindings(total)}` : ''}
          </span>
          <div className="flex items-center gap-2">
            <select className="select" value={query.pageSize} aria-label="Riadkov na stránku" onChange={(e) => update({ pageSize: Number(e.target.value) })}>
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} na stránku{n === FINDINGS_PAGE_SIZE_DEFAULT ? '' : ''}</option>)}
            </select>
            <button type="button" className="chip" disabled={query.page <= 1} onClick={() => update({ page: query.page - 1 }, true)}>← Predošlá</button>
            <span className="mono text-[11.5px] text-dim">{query.page} / {pages}</span>
            <button type="button" className="chip" disabled={query.page >= pages} onClick={() => update({ page: query.page + 1 }, true)}>Ďalšia →</button>
          </div>
        </div>
      </div>
    </>
  );
}

function Th({ label, sort, q, onSort }: { label: string; sort: FindingsSort; q: FindingsQuery; onSort: (s: FindingsSort) => void }) {
  const on = q.sort === sort;
  return (
    <th aria-sort={on ? (q.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className={on ? 'on' : ''} onClick={() => onSort(sort)}>
        {label}
        <span aria-hidden className={on ? '' : 'invisible'}>{q.dir === 'asc' ? '↑' : '↓'}</span>
      </button>
    </th>
  );
}

function Row({ f, onOpen }: { f: Finding; onOpen: () => void }) {
  const closed = f.status !== 'OPEN';
  let status: ReactNode = null;
  if (closed) {
    status = <span className="ml-2 pill bg-raised text-faint">{STATUS_LABEL[f.status]}</span>;
  }
  return (
    <tr className={`click ${closed ? 'opacity-60' : ''}`} onClick={onOpen}>
      <td className="w-[130px]"><SeverityBadge severity={f.severity} /></td>
      <td>
        <div>
          <Link to={`/vulnerabilities/${f.id}`} className="text-ink hover:text-ink" onClick={(e) => e.stopPropagation()}>{f.title}</Link>
          {status}
        </div>
        <div className="sub">{f.component.slug} {f.installedVersion}{f.component.type !== 'plugin' ? ` · ${TYPE_ONE[f.component.type]}` : ''}</div>
      </td>
      <td className="mono w-[150px] text-dim" title={f.site.name}>{f.site.host}</td>
      <td className="mono w-[145px] text-dim">{f.cve ?? '—'}</td>
      <td className={`mono w-[80px] font-medium ${f.cvss === null ? 'text-ghost' : SEVERITY[f.severity].text}`}>{f.cvss === null ? '—' : f.cvss.toFixed(1)}</td>
      <td className="w-[110px] text-xs">
        {f.patchedIn
          ? <span className="mono inline-flex items-center gap-[5px] text-ok"><Icon name="up" size={12} />{f.patchedIn}</span>
          : <span className="text-faint" title="Katalóg zatiaľ nepozná opravenú verziu vyššiu než nainštalovanú">nie je</span>}
      </td>
      <td className="w-[100px] whitespace-nowrap text-[11.5px] text-faint" title={formatDateTime(f.firstDetectedAt)}>{timeAgo(f.firstDetectedAt)}</td>
    </tr>
  );
}
